<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import Input from './OdooInput.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();

const selectedModel = ref('');
const uniqueKey = ref('');
const uniqueValue = ref('');
const keyMapping = ref({});
const selectedFields = ref([{ field: '', value: '' }]);

const models = computed(() => store.state.odoo.models);
const fields = computed(() => store.state.odoo.fields);

const hasFields = computed(() => Object.keys(fields.value).length > 0);

watch(selectedModel, newModel => {
  if (newModel) {
    store.dispatch('odoo/fetchOdooFields', newModel);
  }
});

watch(
  () => props.modelValue,
  newValue => {
    if (newValue) {
      selectedModel.value = newValue.model || '';
      uniqueKey.value = newValue.uniqueKey || '';
      uniqueValue.value = newValue.uniqueValue || '';
      keyMapping.value = newValue.keyMapping || {};
      selectedFields.value = Object.keys(newValue.keyMapping || {}).map(
        key => ({
          field: key,
          value: newValue.keyMapping[key],
        })
      );
      if (selectedFields.value.length === 0) {
        selectedFields.value = [{ field: '', value: '' }];
      }
    }
  },
  { immediate: true }
);

const addField = () => {
  selectedFields.value.push({ field: '', value: '' });
};

const updateField = (index, key, value) => {
  selectedFields.value[index][key] = value;
  updateModelValue();
};

const updateUniqueValue = () => {
  keyMapping.value[uniqueKey.value] = uniqueValue.value;
  updateModelValue();
};

const updateModelValue = () => {
  const keyMappingValue = {};
  selectedFields.value.forEach(item => {
    if (item.field) {
      keyMappingValue[item.field] = item.value;
    }
  });
  emit('update:modelValue', {
    model: selectedModel.value,
    uniqueKey: uniqueKey.value,
    uniqueValue: uniqueValue.value,
    keyMapping: keyMappingValue,
  });
};

const removeField = index => {
  if (selectedFields.value.length > 1) {
    selectedFields.value.splice(index, 1);
    updateModelValue();
  }
};

onMounted(() => {
  store.dispatch('odoo/fetchOdooModels');
});
</script>

<template>
  <div class="mt-4">
    <label for="model-select">Select Odoo Model:</label>
    <select
      id="model-select"
      v-model="selectedModel"
      @change="updateModelValue"
    >
      <option v-for="model in models" :key="model.model" :value="model.model">
        {{ model.name }}
      </option>
    </select>

    <div v-if="hasFields">
      <p>Where</p>
      <div class="flex flex-row items-baseline gap-2">
        <select id="unique-key" v-model="uniqueKey" @change="updateModelValue">
          <option v-for="(field, key) in fields" :key="key" :value="key">
            {{ field.string }} ({{ key }})
          </option>
        </select>
        <p>is</p>
        <Input
          v-model="uniqueValue"
          class="w-full"
          placeholder="Enter Value"
          @change="updateUniqueValue"
        />
      </div>

      <p>Update</p>
      <div v-for="(item, index) in selectedFields" :key="index">
        <div class="flex flex-row items-baseline gap-2">
          <select
            v-model="item.field"
            @change="updateField(index, 'field', $event.target.value)"
          >
            <option v-for="(field, key) in fields" :key="key" :value="key">
              {{ field.string }}
            </option>
          </select>
          <p>to</p>
          <Input
            v-model="item.value"
            class="w-full"
            placeholder="Enter Value"
            @change="updateField(index, 'value', $event.target.value)"
          />
          <woot-button
            icon="dismiss-circle"
            variant="smooth"
            size="tiny"
            color-scheme="alert"
            @click="removeField(index)"
          />
        </div>
      </div>

      <button @click="addField">Add Field</button>
    </div>
  </div>
</template>

<style scoped>
/* Add your styles here */
</style>
